/**
 * The player's right column: the set the film shows, then the discussion under it. The set block
 * holds its loaded height from the first frame and waits for every read its chips come from, so
 * nothing below it moves when they land.
 */

import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import skel from '~/shared/ui/skeleton.module.css';
import { ClockGlyph, FilmGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ClipPlayback } from '../lib/useClipPlayback';
import type { ClipPlayerData } from '../lib/useClipPlayerData';
import type { PlayerHeader } from '../model/playerView';

import styles from './ClipPlayer.module.css';
import { PlayerDiscussion } from './PlayerDiscussion';

interface PlayerAsideProps {
  readonly clipId: number;
  readonly data: ClipPlayerData;
  readonly playback: ClipPlayback;
  /** Theatre mode folds the column away without unmounting it, so a draft survives. */
  readonly folded: boolean;
  readonly status: string;
  readonly onStatus: (status: string) => void;
  readonly onTyping: () => void;
  readonly onBackToFilm: () => void;
  readonly composerRef: Ref<HTMLTextAreaElement>;
}

export function PlayerAside({
  clipId,
  data,
  playback,
  folded,
  status,
  onStatus,
  onTyping,
  onBackToFilm,
  composerRef,
}: PlayerAsideProps) {
  const { t } = useTranslation();
  const setGate = useSkeletonGate(data.pending);
  const { header } = data.view;

  return (
    <aside className={`${String(styles.aside)} ${folded ? String(styles.folded) : ''}`}>
      <div aria-busy={setGate.waiting} className={`${String(styles.setBlock)} ${setGate.reveal}`}>
        {setGate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clipPlayer.states.loading')}</span>
            {setGate.skeleton ? (
              <>
                <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelTitle)}`} />
                <i className={`${String(skel.bar)} ${String(styles.skelChips)}`} />
              </>
            ) : null}
          </>
        ) : header === null ? null : (
          <SetBlock header={header} />
        )}
      </div>

      {data.gone ? null : (
        <PlayerDiscussion
          clipId={clipId}
          composerRef={composerRef}
          data={data}
          onBackToFilm={onBackToFilm}
          onStatus={onStatus}
          playback={playback}
          onTyping={onTyping}
        />
      )}
      <p className={`${String(styles.status)} ${status === '' ? String(styles.statusIdle) : ''}`} role="status">
        {status}
      </p>
    </aside>
  );
}

function SetBlock({ header }: { readonly header: PlayerHeader }) {
  const { t } = useTranslation();
  return (
    <>
      {header.setLine === null ? (
        <>
          <span className={styles.eyebrow}>{t('web:clipPlayer.set.outside')}</span>
          <h3 className={`${String(styles.setTitle)} ${String(styles.setTitleSmall)}`}>{header.title}</h3>
          <p className={styles.setNote}>{header.outsideNote}</p>
        </>
      ) : (
        <>
          <div className={styles.setTop}>
            <span className={styles.eyebrow}>{header.setLine}</span>
            {header.sessionChip === null ? null : <span className={styles.chip}>{header.sessionChip}</span>}
          </div>
          <h3 className={styles.setTitle}>{header.title}</h3>
          {header.chips.length === 0 ? null : (
            <ul aria-label={t('web:clipPlayer.set.sets')} className={styles.setChips}>
              {header.chips.map((chip) => (
                <li
                  className={`${String(styles.setChip)} ${chip.filmed ? String(styles.setChipFilmed) : ''}`}
                  key={chip.setId}
                >
                  {chip.filmed ? <FilmGlyph className={styles.glyphSmall} /> : null}
                  {chip.label}
                </li>
              ))}
            </ul>
          )}
          {header.e1rmLine === null ? null : <p className={styles.setNote}>{header.e1rmLine}</p>}
        </>
      )}
      <span className={`${String(styles.by)} ${header.by.isYou ? String(styles.byYou) : ''}`}>
        {t('web:clipPlayer.set.by')}
        <AvatarChip avatarUrl={header.by.avatarUrl} initials={header.by.initials} variant="clipSmall" />
        {header.by.name}
      </span>
      {header.expires === null ? null : (
        <p className={styles.expiry}>
          <ClockGlyph className={styles.expiryGlyph} />
          {header.expires}
        </p>
      )}
    </>
  );
}
